import React, { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "../../hooks/useTranslation";
import { useScope } from "../../hooks/useScope";
import { getBankTier } from "../../data/banks";
import {
  getUserCreditCards,
  createCreditCard,
  updateCreditCard,
  deleteCreditCard,
} from "../../services/creditCardService";
import CardList from "./CardList";
import CardForm from "./CardForm";
import Modal from "../common/Modal";
import "./PersonalCards.css";

const PersonalCards = () => {
  const { currentUser } = useAuth();
  const t = useTranslation();
  const { isClientView, isManaged, mentorId, effectiveUserId, actualUserId } =
    useScope();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [sortBy, setSortBy] = useState("dateCreated");
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    show: false,
    id: null,
  });

  useEffect(() => {
    if (currentUser) {
      loadCards();
    }
  }, [currentUser, isClientView, isManaged, mentorId, effectiveUserId]);

  const loadCards = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    try {
      // Determine scope based on current view mode
      // Mentor masquerade: mentor viewing client's data (isManaged && !isClientView)
      // Client view: client viewing mentor's data (isManaged && isClientView)
      // Personal view: user's own data (no management)

      const isMentorMasquerade = isManaged && !isClientView;
      const isInClientView = isManaged && isClientView;

      let queryUserId = currentUser.uid;
      let scopeOptions = { personalOnly: true };

      if (isMentorMasquerade && effectiveUserId) {
        // Mentor viewing client: query by client's userId, filter by mentor's id
        queryUserId = effectiveUserId;
        scopeOptions = { mentorId: actualUserId }; // actualUserId is the mentor
      } else if (isInClientView && mentorId) {
        // Client viewing mentor's data: query by client's userId, filter by mentorId
        queryUserId = currentUser.uid;
        scopeOptions = { mentorId };
      }

      const userCards = await getUserCreditCards(
        queryUserId,
        "personal",
        null,
        scopeOptions
      );
      setCards(userCards);
    } catch (err) {
      console.error("Error loading cards:", err);
      setError(t("creditCardsInventory.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = () => {
    setEditingCard(null);
    setShowForm(true);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setShowForm(true);
  };

  const handleDeleteCard = async (cardId) => {
    setDeleteConfirmModal({
      show: true,
      id: cardId,
      message: t("creditCardsInventory.deleteConfirm"),
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteCreditCard(deleteConfirmModal.id);
      await loadCards();
      setDeleteConfirmModal({ show: false, id: null, message: "" });
    } catch (err) {
      console.error("Error deleting card:", err);
      setError(t("creditCardsInventory.failedToDelete"));
      setDeleteConfirmModal({ show: false, id: null, message: "" });
    }
  };

  const handleFormSubmit = async (cardData) => {
    if (!currentUser) return;

    try {
      if (editingCard) {
        await updateCreditCard(editingCard.id, cardData);
      } else {
        // Determine mentorId and userId for creating cards based on scope
        const isMentorMasquerade = isManaged && !isClientView;
        const isInClientView = isManaged && isClientView;

        let createUserId = currentUser.uid;
        let cardMentorId = null;

        if (isMentorMasquerade && effectiveUserId) {
          // Mentor creating card for client
          createUserId = effectiveUserId;
          cardMentorId = actualUserId; // actualUserId is mentor
        } else if (isInClientView && mentorId) {
          // Client creating card in mentor's view
          createUserId = currentUser.uid;
          cardMentorId = mentorId;
        }

        await createCreditCard(createUserId, cardData, cardMentorId);
      }
      setShowForm(false);
      setEditingCard(null);
      await loadCards();
    } catch (err) {
      console.error("Error saving card:", err);
      throw err; // Let CardForm handle the error display
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCard(null);
  };

  // Calculate stats
  const totalCards = cards.length;
  const totalCredit = cards.reduce(
    (sum, card) => sum + (card.creditLine || 0),
    0
  );
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Filter and sort cards
  const filteredCards = cards
    .filter((card) => {
      const matchesSearch =
        !searchQuery ||
        card.bankName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.cardSubType?.toLowerCase().includes(searchQuery.toLowerCase());
      // Derive bankTier from bankName if bankTier is not set
      const cardBankTier = card.bankTier || (card.bankName ? getBankTier(card.bankName) : null);
      const matchesTier = !filterTier || cardBankTier == filterTier;
      return matchesSearch && matchesTier;
    })
    .sort((a, b) => {
      if (sortBy === "creditLimit") {
        return (b.creditLine || 0) - (a.creditLine || 0);
      } else if (sortBy === "bankName") {
        return (a.bankName || "").localeCompare(b.bankName || "");
      } else {
        // dateCreated (default)
        const dateA = a.dateCreated?.toDate
          ? a.dateCreated.toDate()
          : new Date(a.dateCreated || 0);
        const dateB = b.dateCreated?.toDate
          ? b.dateCreated.toDate()
          : new Date(b.dateCreated || 0);
        return dateB - dateA;
      }
    });

  if (showForm) {
    return (
      <div className="personal-cards">
        <CardForm
          card={editingCard}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  return (
    <div className="personal-cards">
      <div className="personal-cards-header">
        <div>
          <h2>{t("creditCardsInventory.personalCardsTitle")}</h2>
          <p>{t("creditCardsInventory.personalCardsSubtitle")}</p>
        </div>
        <button className="btn-add-card" onClick={handleAddCard}>
          {t("creditCardsInventory.addCard")}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadCards}>{t("creditCardsInventory.retry")}</button>
        </div>
      )}

      {/* Quick Stats */}
      {totalCards > 0 && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-label">
              {t("creditCardsInventory.totalCards")}
            </span>
            <span className="stat-value">{totalCards}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">
              {t("creditCardsInventory.totalCredit")}
            </span>
            <span className="stat-value">{formatCurrency(totalCredit)}</span>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      {totalCards > 0 && (
        <div className="search-filter-bar">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder={t("creditCardsInventory.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            className="filter-select"
          >
            <option value="">{t("creditCardsInventory.allTiers")}</option>
            <option value="1">{t("creditCardsInventory.tier1")}</option>
            <option value="2">{t("creditCardsInventory.tier2")}</option>
            <option value="3">{t("creditCardsInventory.tier3")}</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="dateCreated">
              {t("creditCardsInventory.sortByDate")}
            </option>
            <option value="creditLimit">
              {t("creditCardsInventory.sortByCreditLimit")}
            </option>
            <option value="bankName">
              {t("creditCardsInventory.sortByBankName")}
            </option>
          </select>
        </div>
      )}

      <CardList
        cards={filteredCards}
        onEdit={handleEditCard}
        onDelete={handleDeleteCard}
        isLoading={loading}
      />

      {/* Error Modal */}
      {error && (
        <Modal
          isOpen={true}
          onClose={() => setError(null)}
          type="error"
          title={t("creditCardsInventory.errorTitle", "Error")}
          message={error}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModal.show}
        onClose={() =>
          setDeleteConfirmModal({ show: false, id: null, message: "" })
        }
        type="warning"
        title={t("creditCardsInventory.confirmDeleteTitle", "Confirm Delete")}
        message={deleteConfirmModal.message}
        confirmLabel={t("creditCardsInventory.delete", "Delete")}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default PersonalCards;
